<template>
  <ChartCard
    :data-widget-id="widgetId"
    @mouseenter="handleMouseEnter"
  >
    <ChartHeader
      :label="itemLabel"
      :tooltip-key="tooltipKey"
      class="mb-2"
    >
      <template #rightSide>
        <RequisitionReportWidgetCommentsToggle
          class="mr-3"
          :widget-id="widgetId"
          :req-id="reqId"
          :req-uuid="reqUuid"
          :screenshot-selector="`[data-widget-id='${widgetId}']`"
          :thread-name="title"
        />
        <ChartDownload
          :download-id="widgetId"
          :file-name="`${projectName}-requisition-report-${widgetId}-${today}`"
        />
      </template>
    </ChartHeader>
    <ChartContent
      :loading="pageLoading"
      type="horizontalBarChart"
      height="235px"
      class="req-source-chart-content"
      :empty-data="noWidgetData"
    >
      <BarChart
        :id="widgetId"
        :data="chartData"
        :options="chartOptions"
        :plugins="plugins"
      />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { useAppLoading } from '@/stores/app/loading';
import { useTracking } from '@/stores/tracking/tracking';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { startCase, isEmpty, cloneDeep } from 'lodash-es';
import {
  dataLabelsConfig,
  flippedAxisTooltipPluginConfig,
  xScaleWithPercentFormatConfig,
  basicYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';
import { insightsColorMap } from '@/api/helpers/insights/colorMap';
import ChartDownload from '@/components/insights/Chart/ChartDownload.vue';
import { format } from 'date-fns';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';
import RequisitionReportWidgetCommentsToggle from './RequisitionReportWidgetCommentsToggle.vue';

const widgetId = 'requisition-source';
const tooltipKey = 'single-req-source';
const itemLabel = 'What source categories do our applications come from?';
const plugins = [ChartDataLabels];

defineProps({
  projectName: { type: String, default: '' },
});

const today = format(new Date(), READABLE_FILE_NAME_FORMAT);

const {
  source, reqId, reqUuid, title,
} = storeToRefs(useRequisitionReport());
const { pageLoading } = storeToRefs(useAppLoading());

const noWidgetData = computed(() => isEmpty(source.value)
  || Object.keys(source.value).every((key) => !source.value[key]));

const chartData = computed(() => {
  const sourceToUse = cloneDeep(source.value);

  delete sourceToUse.total;

  const total = Object.values(sourceToUse).reduce((acc, curr) => acc + curr, 0);

  // Sort the categories in descending order
  const sorted = Object.entries(sourceToUse)
    .sort((a, b) => b[1] - a[1])
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  const colorMap = insightsColorMap();

  const labels = Object.keys(sorted).map((key) => startCase(key));

  return {
    labels,
    datasets: [
      {
        data: total ? Object.values(sorted).map((value) => (value / total) * 100) : [],
        backgroundColor: labels.map((label) => colorMap[label]),
        borderWidth: 0,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: flippedAxisTooltipPluginConfig(chartData, widgetId),
    datalabels: dataLabelsConfig,
  },
  scales: {
    x: xScaleWithPercentFormatConfig,
    y: basicYScaleConfig,
  },
}));

const handleMouseEnter = () => {
  useTracking().trackName({
    name: 'Requisition Report: Application Categories Bar Chart Hovered',
  });
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

:deep(.chart-error-image) {
  margin-top: 30px;
}

.req-source-chart-header {
  margin-bottom: 8px;
}

.req-source-chart-content {
  padding: 0 24px;
}
</style>
